import React, { useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import SampleTagPDF from '../SampleTagPDF/SampleTagPDF';
import { SampleTagResponse } from '../types';
import {
  needsCorsProxy,
  extractFileKey,
  convertArrayBufferToDataUrl,
  reencodeToPng,
} from '../utils/imageUtils';
import { downloadBlob } from '../utils/pdfUtils';
import SampleTagsAPI from '../../../../Lib/Api/sample-tags';

export const usePDFDownload = (userId: string) => {
  /**
   * Converts images to data URLs for PDF generation.
   */
  const convertImageToDataUrl = useCallback(
    async (productImage: string): Promise<string | null> => {
      try {
        let urlToFetch: string;

        if (productImage.startsWith('https://')) {
          const needsProxy = needsCorsProxy(productImage);
          if (needsProxy) {
            const results = await SampleTagsAPI.getImagesViaProxy(userId, [
              productImage,
            ]);
            const result = results[0];

            if (result?.data && !result.error) {
              const dataUrl = `data:${result.contentType || 'image/jpeg'};base64,${result.data}`;
              try {
                return await reencodeToPng(dataUrl);
              } catch {
                return dataUrl;
              }
            }
            return null;
          }
          urlToFetch = productImage;
        } else {
          const fileKey = extractFileKey(productImage);
          if (!fileKey) {
            console.error('Could not extract file key from:', productImage);
            return null;
          }

          const signedUrlData = await SampleTagsAPI.generatePhotoReadUrl(
            userId,
            fileKey,
          );
          if (!signedUrlData?.signedUrl) {
            console.error('Could not get signed URL for:', fileKey);
            return null;
          }

          urlToFetch = signedUrlData.signedUrl;
        }

        const response = await fetch(urlToFetch);
        if (!response.ok) {
          console.error('Failed to fetch image:', response.statusText);
          return null;
        }

        const arrayBuffer = await response.arrayBuffer();
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          console.error('No image data received');
          return null;
        }

        const dataUrl = await convertArrayBufferToDataUrl(arrayBuffer);
        try {
          return await reencodeToPng(dataUrl);
        } catch {
          return dataUrl;
        }
      } catch (error) {
        console.error('Error converting image to data URL:', error);
        return null;
      }
    },
    [userId],
  );

  const generatePDFBlob = useCallback(
    async (tagData?: SampleTagResponse) => {
      try {
        let enhancedTagData = tagData;

        if (tagData?.product_image) {
          const dataUrl = await convertImageToDataUrl(tagData.product_image);
          if (dataUrl) {
            enhancedTagData = {
              ...tagData,
              productImageDataUrl: dataUrl,
            };
          } else {
            console.warn(
              'Failed to convert image to data URL, PDF will render without image',
            );
          }
        }

        if (!enhancedTagData) {
          throw new Error('No tag data provided for PDF generation');
        }

        const blob = await pdf(
          <SampleTagPDF tagData={enhancedTagData} />,
        ).toBlob();

        return blob;
      } catch (error) {
        console.error('Error generating PDF blob:', error);
        throw error;
      }
    },
    [convertImageToDataUrl],
  );

  const downloadPDF = useCallback(
    async (tagData?: SampleTagResponse) => {
      try {
        const blob = await generatePDFBlob(tagData);
        const timestamp = new Date().toISOString().split('T')[0];
        downloadBlob(blob, `sample-tag-${timestamp}.pdf`);

        return true;
      } catch (error) {
        console.error('Error downloading PDF:', error);
        return false;
      }
    },
    [generatePDFBlob],
  );

  const printPDF = useCallback(
    async (tagData?: SampleTagResponse) => {
      try {
        const blob = await generatePDFBlob(tagData);

        const url = URL.createObjectURL(blob);

        const printWindow = window.open(url, '_blank');

        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }

        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 10000);

        return true;
      } catch (error) {
        console.error('Error printing PDF:', error);
        return false;
      }
    },
    [generatePDFBlob],
  );

  return {
    downloadPDF,
    printPDF,
  };
};

export default usePDFDownload;
